import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from 'src/auth/firebase';
import { getDisplayName } from 'src/auth/displayName';
import { useFirebaseAuth } from 'src/auth/FirebaseAuthContext';
import { InteractionStatus } from 'src/auth/firebaseMsalCompat';
import { useUserProfile } from 'src/hooks/useUserProfile';
import { posthog } from '../../services/telemetry';

/**
 * NavBar auth hook (Firebase-backed). Exposes `handleLogin`, `handleLogout`,
 * `inProgress`, `isAuthenticated`, and the display `name`.
 */
export const useAuth = () => {
  const navigate = useNavigate();
  const { user, isLoaded } = useFirebaseAuth();
  const { profile, eligible } = useUserProfile();
  const profilePending = eligible && !profile;

  // Database name once loaded. While that load is in flight, show nothing:
  // the Firebase name can differ (shortened Google names, a failed copy).
  // Guests and unverified users have no profile row and keep the Firebase name.
  const name = user
    ? getDisplayName({
        profileName: profile?.name ?? null,
        firebaseUser: user,
        fallback: 'Unknown User',
        profilePending,
      }) || undefined
    : undefined;

  // Identify in PostHog on (re)load of an authenticated session. Sign-in /
  // sign-up pages identify on first auth; this covers cached-session reloads.
  useEffect(() => {
    if (user) {
      try {
        posthog.identify(user.uid, {
          email: user.email ?? undefined,
        });
      } catch (err) {
        console.warn('PostHog identify failed:', err);
      }
    }
  }, [user]);

  const handleLogin = async () => {
    navigate('/sign-in');
  };

  const handleLogout = async () => {
    try {
      // Reset before sign-out so the next user on this device doesn't inherit
      // the logged-out user's PostHog identity.
      try {
        posthog.reset();
      } catch (err) {
        console.warn('PostHog reset failed:', err);
      }
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Logout failed', error);
    }
  };

  return {
    handleLogin,
    handleLogout,
    inProgress: isLoaded ? InteractionStatus.None : InteractionStatus.Startup,
    name,
    // Anonymous (guest) sign-ins are NOT treated as authenticated, so the
    // NavBar keeps showing "Log In" after a home-page guest upload. Mirrors
    // `useIsAuthenticated()` in firebaseMsalCompat.
    isAuthenticated: !!user && !user.isAnonymous,
    emailVerified: !!user?.emailVerified,
  };
};
